// One place that turns any failure (Apollo/Hasura, axios, fetch, plain
// strings) into a short message a user can act on. Technical detail stays in
// the console; UI shows `friendlyError(err)` (an English i18n key — wrap in t()).

export type ErrorKind = 'network' | 'session' | 'permission' | 'conflict' | 'inUse' | 'validation' | 'notFound' | 'server' | 'unknown';

export const ERROR_MESSAGES: Record<ErrorKind, string> = {
  network: 'Can’t reach the server. Check your connection and try again.',
  session: 'Your session expired. Please log in again.',
  permission: 'You don’t have permission to do that.',
  conflict: 'That already exists. Use a different name or value.',
  inUse: 'This can’t be removed because other records still use it.',
  validation: 'Some values aren’t valid. Check the form and try again.',
  notFound: 'That item no longer exists. Refresh the page.',
  server: 'The server had a problem. Try again in a moment.',
  unknown: 'Something went wrong. Please try again.'
};

const SESSION_CODES = ['invalid-jwt', 'invalid-headers', 'jwt-invalid-claims', 'unauthorized'];
const PERMISSION_CODES = ['access-denied', 'permission-error', 'forbidden'];
const VALIDATION_CODES = ['validation-failed', 'parse-failed', 'data-exception', 'bad-request', 'invalid-input'];
const NOT_FOUND_CODES = ['not-exists', 'not-found'];

const fromStatus = (status?: number): ErrorKind | null => {
  if (!status) return null;
  if (status === 401) return 'session';
  if (status === 403) return 'permission';
  if (status === 404) return 'notFound';
  if (status === 409) return 'conflict';
  if (status === 400 || status === 422) return 'validation';
  if (status >= 500) return 'server';
  return null;
};

const fromText = (text: string): ErrorKind | null => {
  const s = text.toLowerCase();
  if (/jwt|token.*expired|expired.*token|login_required|missing refresh token/.test(s)) return 'session';
  if (/uniqueness violation|duplicate key|already exists/.test(s)) return 'conflict';
  if (/foreign key violation|violates foreign key/.test(s)) return 'inUse';
  if (/permission|not allowed|access denied|forbidden/.test(s)) return 'permission';
  if (/failed to fetch|network ?error|networkerror|timeout|econn|load failed/.test(s)) return 'network';
  return null;
};

export const errorKind = (err: any): ErrorKind => {
  if (!err) return 'unknown';
  if (typeof err === 'string') return fromText(err) || 'unknown';

  // Apollo: GraphQL errors from Hasura carry extensions.code.
  const gql: any[] = err.graphQLErrors || (err.extensions ? [err] : []);
  for (const g of gql) {
    const code = String(g?.extensions?.code || '').toLowerCase();
    if (SESSION_CODES.includes(code)) return 'session';
    if (PERMISSION_CODES.includes(code)) return 'permission';
    if (code === 'constraint-violation') return /foreign key/i.test(g.message || '') ? 'inUse' : 'conflict';
    if (VALIDATION_CODES.includes(code)) return 'validation';
    if (NOT_FOUND_CODES.includes(code)) return 'notFound';
    const byText = fromText(String(g?.message || ''));
    if (byText) return byText;
  }

  // Apollo network error, or axios error with a response.
  const net = err.networkError;
  if (net) return fromStatus(net.statusCode || net.status) || 'network';
  const byStatus = fromStatus(err.response?.status ?? err.status ?? err.statusCode);
  if (byStatus) return byStatus;
  // axios: request sent, no response.
  if (err.isAxiosError && !err.response) return 'network';
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'network';

  return fromText(String(err.message || err.error || '')) || 'unknown';
};

export const friendlyError = (err: any): string => ERROR_MESSAGES[errorKind(err)];

// True for a failed GraphQL *mutation* marked by the caller as handled in its
// own UI, so the global link doesn't toast it a second time.
export const HANDLED = { handlesErrors: true };
